import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { css, cx } from '@eti/styles';

import type { HeadingTypes } from '@etg/wings';
import { Stack, Text, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { Notification, variants } from '@eti/components';
import { EventSegmentType, TripCharacteristics } from '@eti/schema-types';
import TitledCard from '../../../../common/components/TitledCard';
import TripEventBadge from '../../../../widgets/trip-event/TripEventBadge';
import type { TripInfoType } from '../../../../widgets/trip-event/constants';
import { EVENT_SEGMENT_TYPE_TO_TITLE_KEY } from '../../../../widgets/trip-event/constants';
import StopoverInRomeContent from '../../../../widgets/trip-event/StopoverInRomeContent';
import { bulletStyles } from '../../../../utils/styleUtils';
import { useOrderDetails } from '../../containers/OrderDetailsContext';

const cardStyles = css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.button.borderRadius};
`;

export interface TripEventsCardsProps {
  eventTypes: TripInfoType[];
  headingAs?: `h${HeadingTypes['level']}`;
  headingLevel?: HeadingTypes['level'];
  shouldAddBorder?: boolean;
}

const TripEventsCards = ({
  eventTypes,
  headingAs,
  headingLevel,
  shouldAddBorder = true,
}: TripEventsCardsProps) => {
  const { t } = useTranslation();
  const { tripCharacteristics } = useOrderDetails();

  const EVENT_SEGMENT_TYPE_TO_CONTENT: Record<TripInfoType, ReactNode> = useMemo(() => {
    return {
      [EventSegmentType.OvernightStay]: (
        <Text asHtml> {t('OrderDetails.TravelInformation.OvernightStopover.Content')}</Text>
      ),
      [EventSegmentType.ChangeOfAirport]: (
        <Text asHtml>{t('OrderDetails.TravelInformation.ChangeOfAirport.Content')}</Text>
      ),
      [EventSegmentType.TransferToAirport]: (
        <Text asHtml>{t('OrderDetails.TravelInformation.TransferToAirport.Content')}</Text>
      ),
      [EventSegmentType.TransferToBus]: (
        <Text asHtml> {t('OrderDetails.TravelInformation.ChangeToBus.Content')}</Text>
      ),
      [EventSegmentType.TransferToTrain]: (
        <Text asHtml>{t('OrderDetails.TravelInformation.ChangeToTrain.Content')}</Text>
      ),
      [EventSegmentType.SelfTransfer]: (
        <Stack spacing={8}>
          <Text asHtml className={bulletStyles()}>
            {t('OrderDetails.TravelInformation.SelfTransfer.Content')}
          </Text>
          {tripCharacteristics.includes(
            TripCharacteristics.VirtualInterliningWithShortConnectionTime,
          ) ? (
            <Notification
              dataTestId="self-guarantee-no-bag-notification"
              variant={variants.WARNING}
            >
              <Text asHtml>
                {t('OrderDetails.TravelInformation.SelfTransfer.Guarantee.Notification')}
              </Text>
            </Notification>
          ) : (
            <Text asHtml>{t('OrderDetails.Notification.SelfTransfer.Content')}</Text>
          )}
        </Stack>
      ),
      [EventSegmentType.ShortStopover]: (
        <StopoverInRomeContent type={EventSegmentType.ShortStopover} />
      ),
      [EventSegmentType.OneDayStopover]: (
        <StopoverInRomeContent type={EventSegmentType.OneDayStopover} />
      ),
      [EventSegmentType.OneNightStopover]: (
        <StopoverInRomeContent type={EventSegmentType.OneNightStopover} />
      ),
      [EventSegmentType.TwoNightsStopover]: (
        <StopoverInRomeContent type={EventSegmentType.TwoNightsStopover} />
      ),
      TECHNICAL_STOP: (
        <Text asHtml>{t('OrderDetails.TravelInformation.TechnicalStop.Content')}</Text>
      ),
    };
  }, [t, tripCharacteristics]);

  const selfTransferIncluded = eventTypes.includes(EventSegmentType.SelfTransfer);
  const restEventTypes = eventTypes.filter(
    (eventType) => eventType !== EventSegmentType.SelfTransfer,
  );

  const sortedEventTypes: TripInfoType[] = selfTransferIncluded
    ? [EventSegmentType.SelfTransfer, ...restEventTypes]
    : restEventTypes;

  return (
    <>
      {sortedEventTypes.map((type) => (
        <TitledCard
          className={cx({ [cardStyles]: shouldAddBorder })}
          data-testid={`${type}-event-section`}
          headingAs={headingAs}
          headingLevel={headingLevel}
          icon={<TripEventBadge type={type} />}
          id={type}
          key={`${type}-event-section`}
          title={t(EVENT_SEGMENT_TYPE_TO_TITLE_KEY[type])}
        >
          {EVENT_SEGMENT_TYPE_TO_CONTENT[type]}
        </TitledCard>
      ))}
    </>
  );
};

export default TripEventsCards;
